import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import Header from "../../../Header1";
import log from "../../../../utils/log";
import { API_URL } from "../../../../api"
import Loader from "../../../Utility/Loader";

import UserAvatar from "../UserAvatar";
import { formatValue } from "../../../../utils/utils";
import UserProfileDetails from "../UserProfileDetails";
import * as adminUserAction from "../../../../actions/admin/user";
import { approvalStatuses, reasons } from "../../../../utils/constants";
import { getProfileSelfieImageUrls } from "../../../../utils/serverUtils";

const ReportDetails = () => {
  const { id: reportId } = useParams(); // URL param (may be reportId or userId)
  const navigate = useNavigate();
  const location = useLocation();

  // Use passed state data as primary source for report info if API fetch fails logic relies on it
  const passedReportData = location.state?.reportData;

  const [details, setDetails] = useState(null);
  const [reportDetails, setReportDetails] = useState(null); // specific report details
  const [update, setUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  const [reason, setReason] = useState("");
  const [quesAns, setQuesAns] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [customReason, setCustomReason] = useState("");
  const [selectedReason, setSelectedReason] = useState("");

  const handleSelectChange = event => {
    const selected = event.target.value;
    setSelectedReason(selected);
    setCustomReason("");
    setReason(selected);
  };

  const handleCustomReasonChange = event => {
    const customText = event.target.value;
    setCustomReason(customText);
    setSelectedReason("");
    setReason(customText);
  };

  const [images, setImages] = useState({
    main: null,
    first: null,
    second: null,
  });

  // Fetch Report Details first to get the User ID
  const fetchReportContext = useCallback(async () => {
    try {
        setLoading(true);
        // Try fetching as report ID first
        const response = await fetch(`${API_URL}admin/report/${reportId}`);
        if (!response.ok) {
            // If fetching report fails (e.g. 404), assume reportId is actually a userId (fallback from list)
            console.warn("Report fetch failed, assuming ID is userId");
            return reportId; 
        }
        const data = await response.json();
        if (!data.user_id) {
             // If response doesn't look like a report, assume it's user data or handle accordingly
             return reportId;
        }
        setReportDetails(data);
        return data.user_id; // Return offender ID from report
    } catch (err) {
        log.error("Error fetching report details, using ID as userId:", err);
        return reportId; // Fallback
    }
  }, [reportId]);

  const ImageURL = async (userId) => {
    if (!userId) return;
    try {
      const response = await adminUserAction.getUserMedias(userId);
      if (response) {
        const profileImages = getProfileSelfieImageUrls(response);
        setImages(profileImages);
      }
    } catch (error) {
      log.error("Error saving images:", error);
    }
  };

  const detailsfetchData = async (userId) => {
    if (!userId) return;
    try {
      const response = await adminUserAction.getCustomerByID(userId);

      // Clone the object before mutating
      const sanitizedDetails = { ...response };

      // Remove unwanted keys
      [
        "completed",
        "email_verified_at",
        "birthday",
        "gender",
        "want_gender",
        "created_at",
        "updated_at",
        "popup_promo_shown_stage",
      ].forEach(key => delete sanitizedDetails[key]);

      setDetails(sanitizedDetails);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const quesAnsfetchData = async (userId) => {
    if (!userId) return;
    try {
      const response = await fetch(
        `${API_URL}admin/users/user/questions/${userId}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          log.error("Resource not found");
          setQuesAns([]);
          return;
        }
        throw new Error(`Failed to fetch questions: ${response.statusText}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        setQuesAns([]);
      } else {
        setQuesAns(data);
      }
    } catch (err) {
      log.error("Error fetching questions:", err);
      setQuesAns([]);
    }
  };

  const GetUserFromUpdateMedia = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const response = await adminUserAction.getUserUpdateRequestedUser(userId);
      setUpdate(response?.length !== 0);
    } catch (err) {
      console.error("Internal Server Error:", err);
    }
  }, []);



  useEffect(() => {
    const init = async () => {
        const userId = await fetchReportContext();
        if (userId) {
            await detailsfetchData(userId);
            await quesAnsfetchData(userId);
            await GetUserFromUpdateMedia(userId);
            await ImageURL(userId);
        }
    };
    init();
  }, [fetchReportContext]);

  const updateStatusReject = async newStatus => {
    try {
      const response = await fetch(`${API_URL}admin/users/updatestatus`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: details.id, approval: newStatus, message: reason }), // Use details.id (user_id)
      });

      if (!response.ok) {
        const errorDetails = await response.text(); // Get error details for debugging
        throw new Error(`Failed to update status: ${errorDetails}`);
      }

      toast.success(`User Status Updated to: ${newStatus}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Delay navigation to allow toast to be seen
      setTimeout(() => {
          handleCloseModal();
          navigate("/admin/user/approval"); 
      }, 1500); 
    } catch (err) {
      log.error(err);
      toast.error(`Update Failed: ${err.message}`);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
        return;
    }

    try {
      // Use the action wrapper that handles Auth headers
      await adminUserAction.deleteUser({ id: details.id });

      // Auto-resolve report so it disappears from unresolved list
      try {
           await fetch(`${API_URL}admin/report/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_ids: [details.id], status: 50 }),
            });
      } catch (e) {
          console.error("Failed to resolve report during delete:", e);
      }

      toast.success("User Deleted Successfully");

      setTimeout(() => {
          navigate("/admin/user/approval");
      }, 1500);

    } catch (err) {
      console.error(err);
      toast.error(`Delete Failed: ${err.message || "Unknown error"}`);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setReason("");
  };

  const handleSaveReason = () => {
    updateStatusReject(30);
  };
  
  const handleResolve = async () => {
     if (window.confirm("Are you sure you want to resolve this user?")) {
        await updateStatusReject(50); // 50 = Resolved
     }
  };
  
  const handleReject = () => {
     setOpenModal(true); // Open modal to enter reason for banning/rejecting
  };



  // Construct display data for report
  const displayReportData = reportDetails || passedReportData;

  if (!details) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <div className="container">
      {loading && <Loader />}
      <Header title="User Details" />
      <div className="row g-4">
        <div className="col-12 col-md-4">
          <div className="d-flex flex-column align-items-center my-3">
            <div className="mb-3 w-100">
              <UserAvatar images={images} />
            </div>

            <h5 className="text-center">
              {`${formatValue("name", details.first_name)} ${formatValue("surname", details.last_name)}`}
            </h5>
            <p className="text-muted text-center">
              {formatValue("status", approvalStatuses[details.approval] || "Unknown Status")}
            </p>
          </div>
        </div>
        <div className="col-12 col-md-8">
          <div className="row g-3">
            {/* User Profile Details */}
            <div className="col-12">


              <UserProfileDetails details={details} />
            </div>

            {/* Question & Answer Section */}
            {quesAns?.length > 0 && (
              <div className="col-12 mb-4">
                <h4 className="mb-3">Question & Answers</h4>
                {quesAns.map((item, index) => (
                  <div key={index} className="mb-3">
                    <h6 className="fw-bold">{item.question}</h6>
                    <p className="mb-0">{item.answer}</p>
                  </div>
                ))}
              </div>
            )}

             {/* Report Information Box */}
             {(displayReportData) && (
                  <div className="col-12 mb-4">
                  <div className="card mb-3 border-danger">
                      <div className="card-header bg-danger text-white">
                          <h5 className="mb-0">Report Details</h5>
                      </div>
                      <div className="card-body">
                           <p><strong>Reason:</strong> {displayReportData.reason || "N/A"}</p>

                           <p><strong>Date:</strong> {displayReportData.created_at || displayReportData.reported_date ? new Date(displayReportData.created_at || displayReportData.reported_date).toLocaleDateString() : "N/A"}</p>

                      </div>
                  </div>
                  </div>
              )}
          </div>

          {/* Footer Button Section */}
          <div className="row g-3 mb-3">
            <div className="d-flex flex-column align-items-center justify-content-center gap-3 mt-4">
              <div className="row gy-2 justify-content-center">
                <div className="hstack gap-2 justify-content-center"> {/* Center aligned actions */}
                  <div className="text-center">
                    <button className="btn btn-primary me-2" onClick={handleResolve}>Resolve</button>
 

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "360px",
            height: "400px",
          },
        }}
      >
        <DialogTitle>What's the reason?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Select a reason</InputLabel>
            <Select
              value={selectedReason}
              onChange={handleSelectChange}
              label="Select a reason"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    width: "calc(360px - 32px)",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              {reasons.map((reason, index) => (
                <MenuItem
                  key={index}
                  value={reason}
                  sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
                >
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            type="text"
            fullWidth
            variant="outlined"
            value={customReason}
            onChange={handleCustomReasonChange}
            multiline
            rows={5}
            sx={{
              marginTop: "16px",
              "& .MuiInputBase-root": {
                height: "150px",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveReason(reason)}
            sx={{
              background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </div>
  );
};

export default ReportDetails;
